import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";
import QuoteCountdown from "../delivery/QuoteCountdown";

// Gate C5A §7 — the Kitchen/Grocery card.
//
// Everything shown here is computed by the backend
// (`retail/delivery/dispatch/staff_guidance.py`) and rendered verbatim.
// The deliberate consequence: a staff member and a support engineer
// reading a log see the SAME sentence, and there is exactly one place to
// change the wording. The only thing this component derives locally is
// the second-by-second countdown, and even that is anchored to the
// server's own `book_uber_by` timestamp.
//
// The card never shows a technical identifier in its primary copy.
// Quote ids and the Uber delivery id suffix live behind
// "Technical details", collapsed by default — available to support,
// invisible during a dinner rush.

function Line({ label, value, testId, strong }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1" data-testid={testId}>
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`text-sm text-slate-900 ${strong ? "font-semibold" : ""}`}>{value ?? "—"}</span>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return "—";
  // Local time, with the zone named. A kitchen reading "8:13" needs to
  // know it is their 8:13 — a bare time on a server-UTC screen has sent
  // real orders out an hour late.
  return new Date(parsed).toLocaleTimeString([], {
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
}

export default function UberQuoteStaffCard({ orderId, onChanged }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requoting, setRequoting] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const [replacement, setReplacement] = useState(null);
  // Guards a double-click during the round-trip. The server's requote
  // lease is the real guarantee (this ref dies with the component); this
  // only keeps the UI honest.
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const { data } = await api.get(
        `/retail/local-delivery/orders/${orderId}/staff-card`,
      );
      setCard(data);
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error("Could not load the delivery card.");
      }
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const requestNewQuote = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRequoting(true);
    try {
      const { data } = await api.post(
        `/retail/local-delivery/orders/${orderId}/replacement-quote`,
      );
      setReplacement(data);
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(
        (det && typeof det === "object" && det.message)
        || "Could not get a new Uber quote right now.",
      );
    } finally {
      inFlight.current = false;
      setRequoting(false);
    }
  };

  if (loading) return null;
  if (!card) return null;

  const expired = card.booking_urgency === "expired" || !card.booking_allowed;
  // Gate C5A-5A — the server decides this, never the browser. False means
  // no money was ever authorized for this order, whatever the quote says.
  // `!== false` so a card from an older backend (field absent) keeps its
  // existing behaviour rather than silently rendering every order as
  // unpaid.
  const actionable = card.operationally_actionable !== false;

  return (
    <div
      className={
        actionable
          ? "rounded-xl border border-slate-200 bg-white p-4"
          : "rounded-xl border border-amber-300 bg-amber-50/60 p-4"
      }
      data-testid="uber-quote-staff-card"
      data-lifecycle-state={card.lifecycle_state}
      data-operationally-actionable={actionable ? "true" : "false"}
    >
      {!actionable ? (
        <p
          className="mb-3 rounded-lg bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-900"
          data-testid="uber-quote-staff-card-not-operational"
        >
          Not an active order — this was never paid for. Do not prepare it
          or book a courier.
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <span
          className="text-sm font-semibold text-slate-900"
          data-testid="uber-quote-staff-card-lifecycle-label"
        >
          {/* Never a bare "Accepted": the backend label always names the
              actor, and keeps naming the customer's acceptance even
              after the provider quote has expired. */}
          {card.lifecycle_label}
        </span>
        {actionable && card.book_uber_by && !card.uber_delivery_booked ? (
          <QuoteCountdown
            deadline={card.book_uber_by}
            label="Book Uber by"
            testId="uber-quote-staff-card-countdown"
          />
        ) : null}
      </div>

      <p
        className="mt-3 text-sm leading-relaxed text-slate-700"
        data-testid="uber-quote-staff-card-guidance"
      >
        {card.guidance}
      </p>

      <div className="mt-3 border-t border-slate-100 pt-2">
        <Line
          label="Customer accepted delivery fee"
          value={card.customer_accepted_delivery_fee}
          testId="uber-quote-staff-card-accepted-fee"
          strong
        />
        <Line
          label="Payment"
          value={card.payment_status_label}
          testId="uber-quote-staff-card-payment"
        />
        <Line
          label="Preparation time"
          value={card.preparation_time_label}
          testId="uber-quote-staff-card-preparation"
        />
        <Line
          label="Order ready by"
          value={fmtTime(card.order_ready_by)}
          testId="uber-quote-staff-card-ready-by"
        />
        <Line
          label="Book Uber by"
          value={fmtTime(card.book_uber_by)}
          testId="uber-quote-staff-card-book-by"
        />
        <Line
          label="Uber delivery"
          value={card.uber_delivery_status}
          testId="uber-quote-staff-card-uber-status"
        />
      </div>

      <div
        className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-900"
        data-testid="uber-quote-staff-card-recommended-action"
      >
        Next: {card.recommended_action}
      </div>

      {replacement ? (
        <div
          className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          role="status"
          data-testid="uber-quote-staff-card-replacement"
        >
          <div>Previous delivery fee: ${Number(replacement.customer_accepted_fee_amount ?? 0).toFixed(2)}</div>
          <div>New Uber fee: ${Number(replacement.new_fee_amount ?? 0).toFixed(2)}</div>
          <div data-testid="uber-quote-staff-card-fee-difference">
            Tenant cost difference: {replacement.fee_difference > 0 ? "+" : ""}
            ${Number(replacement.fee_difference ?? 0).toFixed(2)}
            {" "}({replacement.fee_direction || "unknown"})
          </div>
          {replacement.customer_reapproval_required ? (
            <div className="mt-2 font-semibold" data-testid="uber-quote-staff-card-reapproval">
              Customer approval required — the customer's payment is authorized for the
              original amount only and must not be charged more without their approval.
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Gate C5A-5A — `actionable &&` is load-bearing, not decoration.
          An unpaid order now has `booking_allowed: false`, which makes
          `expired` true, which would otherwise OFFER "Get new Uber quote"
          on an order nobody paid for — spending a real provider quote to
          make an unpayable order look bookable again. The server refuses
          it too; this stops the button existing. */}
      {actionable && expired && !card.uber_delivery_booked ? (
        <button
          type="button"
          onClick={requestNewQuote}
          disabled={requoting}
          className="mt-3 h-10 w-full rounded-lg bg-slate-900 text-sm font-semibold text-white disabled:opacity-60"
          data-testid="uber-quote-staff-card-requote"
        >
          {requoting ? "Getting a new Uber quote…" : "Get new Uber quote"}
        </button>
      ) : null}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowTechnical((v) => !v)}
          className="text-[11px] uppercase tracking-wider font-semibold text-slate-500"
          data-testid="uber-quote-staff-card-technical-toggle"
          aria-expanded={showTechnical}
        >
          Technical details
        </button>
        {showTechnical ? (
          <dl
            className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[11px] text-slate-600"
            data-testid="uber-quote-staff-card-technical"
          >
            {Object.entries(card.technical_details || {}).map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-slate-400">{k}</dt>
                <dd className="break-all">{v === null || v === undefined ? "—" : String(v)}</dd>
              </React.Fragment>
            ))}
          </dl>
        ) : null}
      </div>
    </div>
  );
}
